import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Components } from './styled';
import SearchBar from './components/SearchBar';
import useFormTextState from '../../hooks/useFormTextState';
import { SearchDropdownItem } from '../../hooks/useSearchDropdown';
import DropdownItem from './cells/SelectableItem';
import Placeholder from '../Placeholder';
import { DropdownSelectableItemType, DropdownType } from './types';
import { DEFAULT_MENU_LEFT_OFFSET, DEFAULT_MENU_TOP_OFFSET, DEFAULT_MENU_WIDTH } from '../../utils/styled/dimensions';
import AuthorItem from './cells/AuthorItem';
import { AppSettingsContext } from '../../providers/AppSettingsProvider/context';

const ProjectSearchDropdown = ({
  open,
  anchorEl,
  handleClose,
  dropdownType,
  items,
  selectedItems,
  onItemClick,
  searchPlaceholder,
  facetFilter,
  onNonFacetClick,
  nonFacetItemType,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  dropdownType: DropdownType;
  items: SearchDropdownItem[];
  selectedItems: SearchDropdownItem[];
  onItemClick: (item: SearchDropdownItem) => void;
  searchPlaceholder?: string;
  facetFilter?: (item: SearchDropdownItem) => boolean;
  onNonFacetClick?: (item: SearchDropdownItem, triggerItemClick: (item: SearchDropdownItem) => void) => void;
  nonFacetItemType?: DropdownSelectableItemType;
}) => {
  const { authors } = useContext(AppSettingsContext);

  const _dropdownWidth = DEFAULT_MENU_WIDTH;
  const _offsetLeft = DEFAULT_MENU_LEFT_OFFSET;
  const _offsetTop = DEFAULT_MENU_TOP_OFFSET;

  const filterProps = useFormTextState();
  const [filteredItems, setFilteredItems] = useState<SearchDropdownItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const setFacetItems = (facetFilter: (item: SearchDropdownItem) => boolean) => {
    const facetItems = items?.filter((item) => facetFilter(item));
    if (facetItems?.length > 0) {
      setFilteredItems(facetItems);
    } else {
      setFilteredItems([]);
    }
  };

  useEffect(() => {
    if (filterProps?.value && filterProps?.value?.length > 0) {
      const filteredData = items?.filter((item) => item.searchLabel.includes(filterProps?.value as string));
      setFilteredItems(filteredData);
      setIsSearching(true);
    } else if (facetFilter) {
      setFacetItems(facetFilter);
    } else {
      setFilteredItems(items);
      setIsSearching(false);
    }
  }, [filterProps.value, items]);

  useEffect(() => {
    if (open && facetFilter) {
      setFacetItems(facetFilter);
    } else {
      filterProps.setValue('');
    }
  }, [open]);

  const clearSearch = () => {
    filterProps.setValue('');
  };

  const isFacetItem = useCallback((item: SearchDropdownItem) => facetFilter?.(item), [items, open]);

  const _onItemClick = (item: SearchDropdownItem) => {
    if (facetFilter) {
      const isFacet: boolean = facetFilter(item);
      !isFacet && onNonFacetClick?.(item, onItemClick);
      isFacet && onItemClick(item);
      return;
    }
    onItemClick(item);
  };

  return (
    <Components.Menu
      elevation={0}
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: -_offsetTop,
        horizontal: _dropdownWidth + _offsetLeft,
      }}
      MenuListProps={{
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'aria-labelledby': 'basic-button',
      }}>
      <SearchBar
        value={filterProps.value}
        onChange={filterProps.onChangeText}
        isSearching={isSearching}
        clearValue={clearSearch}
        placeholder={searchPlaceholder && searchPlaceholder?.length > 0 ? searchPlaceholder : 'Search'}
      />
      <Components.ItemsWrapper>
        {filteredItems && filteredItems?.length > 0 ? (
          filteredItems?.map((item, key) => {
            switch (dropdownType) {
              case 'basic':
                return (
                  <DropdownItem
                    key={key}
                    label={item.label}
                    onClick={() => _onItemClick(item)}
                    overrideItemType={!isFacetItem(item) ? nonFacetItemType : undefined}
                    checked={selectedItems?.find((selectedItem) => selectedItem.value === item.value) !== undefined}
                    disableClick={!isFacetItem(item) && !onNonFacetClick}
                  />
                );
              case 'authors':
                return (
                  <AuthorItem
                    key={key}
                    label={item?.label}
                    author={authors?.filter((author) => author?.id === item?.value)?.[0]}
                    checked={selectedItems?.find((selectedItem) => selectedItem.value === item.value) !== undefined}
                    onClick={() => _onItemClick(item)}
                  />
                );
            }
          })
        ) : (
          <Components.PlaceholderWrapper>
            <Placeholder type={'auto'} text={'No entries found'} />
          </Components.PlaceholderWrapper>
        )}
      </Components.ItemsWrapper>
    </Components.Menu>
  );
};

export default ProjectSearchDropdown;
